import { type DocumentNode, useQuery } from "@apollo/client";
import { Button, ControlGroup, MenuItem } from "@blueprintjs/core";
import { MultiSelect } from "@blueprintjs/select";
import { type ItemCreateDrawerProps } from "@package/ui-apollo-components";
import { uniqBy } from "lodash";
import { type ComponentType, useEffect, useMemo, useState } from "react";
import {
  getDisplayForDocumentItem,
  type ItemSelectQuery,
  type ItemSelectQueryVariables,
} from "./item-select-utils";

type ItemMultiSelectProps<
  Q extends ItemSelectQuery,
  V extends ItemSelectQueryVariables,
> = {
  createDrawerElement?: ComponentType<ItemCreateDrawerProps>;
  document: DocumentNode;
  nullable?: boolean;
  variables?: V;
  query?: Q;
  sorted?: boolean;
  activeItemIds?: string[] | null;
  disabled?: boolean;
  onItemSelected?: (id?: string | null) => void;
  onItemRemoved?: (id: string) => void;
};

export function ItemMultiSelect<
  Q extends ItemSelectQuery,
  V extends ItemSelectQueryVariables,
>({
  activeItemIds,
  createDrawerElement: CreateDrawerElement,
  disabled,
  document,
  nullable,
  sorted = true,
  variables,
  onItemSelected,
  onItemRemoved,
}: ItemMultiSelectProps<Q, V>) {
  const [query, setQuery] = useState<string>();
  const [queryDebounced, setQueryDebounced] = useState<string>();
  const {
    data,
    loading: loadingData,
    error: errorData,
  } = useQuery<Q, ItemSelectQueryVariables>(document, {
    variables: {
      page: 0,
      perPage: 1000,
      ...variables,
      filter: {
        ...variables?.filter,
        q: queryDebounced,
      },
    },
  });
  const {
    data: dataItemsById,
    loading: loadingItemsById,
    error: errorItemsById,
  } = useQuery<Q, ItemSelectQueryVariables>(document, {
    variables: {
      page: 0,
      perPage: 1000,
      ...variables,
      filter: {
        ids: activeItemIds,
      },
    },
    skip: !activeItemIds?.length,
  });

  const loading = loadingData || loadingItemsById;
  const error = errorData || errorItemsById;

  const items = useMemo(() => {
    let dataItems = uniqBy(
      [...(data?.items ?? []), ...(dataItemsById?.items ?? [])],
      (item) => item.id,
    );

    if (sorted) {
      dataItems.sort((a, b) => {
        if (!a && b) {
          return 1;
        }
        if (a && !b) {
          return -1;
        }
        return (
          getDisplayForDocumentItem<Q>(a)?.localeCompare(
            getDisplayForDocumentItem<Q>(b) ?? "",
            ["en-US"],
            { caseFirst: "lower" },
          ) ?? 0
        );
      });
    }

    if (nullable) {
      dataItems.splice(0, 0, {
        id: null,
      });
    }

    return dataItems;
  }, [data?.items, dataItemsById?.items, nullable, sorted]);
  const selectedItems = useMemo(
    () => items.filter((item) => activeItemIds?.includes(item?.id ?? "")) ?? [],
    [activeItemIds, items],
  );
  const [isOpenCreateDrawer, setIsOpenCreateDrawer] = useState<boolean>(false);

  useEffect(
    function effectUpdateQueryDebounced() {
      const timeout = setTimeout(() => {
        setQueryDebounced(query);
      }, 300);

      return () => {
        clearTimeout(timeout);
      };
    },
    [query],
  );

  return (
    <ControlGroup>
      <MultiSelect<Q["items"][0]>
        disabled={disabled}
        fill
        items={items}
        itemsEqual={(a, b) => a.id === b.id}
        itemPredicate={(query, item) =>
          getDisplayForDocumentItem<Q>(item)
            ?.toLocaleLowerCase()
            ?.includes(query.toLocaleLowerCase()) ?? false
        }
        onQueryChange={(q) => setQuery(q || undefined)}
        itemRenderer={(item, { handleClick, handleFocus, modifiers }) => (
          <MenuItem
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={item.id}
            onClick={handleClick}
            onFocus={handleFocus}
            roleStructure="listoption"
            selected={activeItemIds?.includes(item.id ?? "")}
            shouldDismissPopover={false}
            text={getDisplayForDocumentItem<Q>(item)}
          />
        )}
        noResults={
          <MenuItem
            disabled={true}
            text={
              error
                ? "Failed loading items"
                : loading
                  ? "Loading..."
                  : "No Items"
            }
            roleStructure="listoption"
          />
        }
        onItemSelect={(item) => {
          if (item.id && selectedItems?.find((i) => i.id === item.id)) {
            onItemRemoved?.(item.id);
          } else {
            onItemSelected?.(item?.id);
          }
        }}
        popoverProps={{
          minimal: true,
        }}
        resetOnSelect
        selectedItems={selectedItems}
        tagRenderer={(item) => getDisplayForDocumentItem<Q>(item)}
        tagInputProps={{
          autoResize: true,
          className: "max-h-52 overflow-auto",
          onRemove: (_, index) => {
            const item = selectedItems?.[index];
            if (item?.id) {
              onItemRemoved?.(item.id);
            }
          },
          tagProps: {
            fill: true,
            intent: "none",
            minimal: true,
          },
        }}
      />
      {CreateDrawerElement && (
        <div className="flex flex-col">
          <ControlGroup>
            <Button
              icon="add"
              intent="primary"
              minimal
              outlined
              onClick={() => setIsOpenCreateDrawer(true)}
            />
          </ControlGroup>
        </div>
      )}
      {CreateDrawerElement && (
        <CreateDrawerElement
          isOpen={isOpenCreateDrawer}
          onClose={() => setIsOpenCreateDrawer(false)}
          onCreated={(id) => {
            onItemSelected?.(id);
          }}
        />
      )}
    </ControlGroup>
  );
}
